import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./utils/styles/customToast.css";
import "./theme/Theme.css";
import "./App.css";
import { useFontsLoaded } from "./theme/useFontsLoaded";
import { Loader } from "./utils";
import ProtectedRoute from "./components/routes/ProtectedRoute";
import PublicRoute from "./components/routes/PublicRoute";
import { DashboardRoute } from "./components/routes/DashboardRoute";
import { useTokenExpiry } from "./hooks/useTokenExpiry";
import UnderMaintenance from "./modules/maintenance/UnderMaintenance";
import AnErrorOccured from "./modules/maintenance/AnErrorOccured";
import PageNotFound from "./modules/maintenance/PageNotFound";
import AccountInactive from "./modules/maintenance/AccountInactive";

// Auth pages — loaded eagerly (first paint, small files)
import Login from "./modules/auth/Login";
import Signup from "./modules/auth/SignUp";
import ForgotPassword from "./modules/auth/ForgotPassword";
import VerifyOtp from "./modules/auth/VerifyOtp";
import SetPassword from "./modules/auth/SetPassword";

// Legal pages
import PrivacyPolicy from "./modules/legal/PrivacyPolicy";
import TermsAndConditions from "./modules/legal/TermsAndConditions";
import CancellationRefundPolicy from "./modules/legal/CancellationRefundPolicy";

// Heavy pages — code-split so they don't inflate the initial bundle
const DashBoard           = lazy(() => import("./modules/dashboard"));
const PublicPostView      = lazy(() => import("./modules/publicShare/PublicPostView"));

const OnBoarding          = lazy(() => import("./modules/onBoarding/onBoarding/OnBoarding"));
const VerificationInProgress = lazy(() => import("./modules/onBoarding/verificationProgress/VerificationInProgress"));
const PaymentStatusPage   = lazy(() => import("./modules/dashboard/payments/PaymentStatusPage"));

const fallback = (
  <Loader variant="spinner" size="large" color="primary" fullScreen text="Loading..." />
);

function App() {
  useTokenExpiry();
  const fontsLoaded = useFontsLoaded();
  const healthStatus = true;

  if (!fontsLoaded || healthStatus === "checking") {
    return fallback;
  }

  if (healthStatus === "maintenance") {
    return <UnderMaintenance />;
  }

  return (
    <>
      <Suspense fallback={fallback}>
        <Routes>
          {/* Public / Auth Routes */}
          <Route path="/"                 element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/login"            element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/signup"           element={<PublicRoute><Signup /></PublicRoute>} />
          <Route path="/business/login"   element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/business/signup"  element={<PublicRoute><Signup /></PublicRoute>} />
          <Route path="/forgot-password"  element={<PublicRoute><ForgotPassword /></PublicRoute>} />
          <Route path="/verify-otp"       element={<PublicRoute><VerifyOtp /></PublicRoute>} />

          {/* Legal Pages (public) */}
          <Route path="/privacy"                    element={<PublicRoute><PrivacyPolicy /></PublicRoute>} />
          <Route path="/terms_conditions"           element={<PublicRoute><TermsAndConditions /></PublicRoute>} />
          <Route path="/refund_policy" element={<PublicRoute><CancellationRefundPolicy /></PublicRoute>} />

          {/* Public Share (no auth needed) */}
          <Route path="/share/post/:id"             element={<PublicPostView />} />

          {/* Maintenance / Status (no auth needed, tiny) */}
          <Route path="/under-maintenance"  element={<UnderMaintenance />} />
          <Route path="/an-error-occurred"  element={<AnErrorOccured />} />
          <Route path="/account-inactive"   element={<AccountInactive />} />

          {/* Protected Routes */}
          <Route path="/set-password"         element={<ProtectedRoute><SetPassword /></ProtectedRoute>} />

          <Route path="/dashboard"             element={<ProtectedRoute><DashboardRoute /></ProtectedRoute>} />
          <Route path="/dashboard/:tab"        element={<ProtectedRoute><DashBoard /></ProtectedRoute>} />
          <Route path="/dashboard/:tab/:id"    element={<ProtectedRoute><DashBoard /></ProtectedRoute>} />
          <Route path="/onboarding"            element={<ProtectedRoute><OnBoarding /></ProtectedRoute>} />
          <Route path="/verification-in-progress" element={<ProtectedRoute><VerificationInProgress /></ProtectedRoute>} />
          <Route path="/payment/status"        element={<ProtectedRoute><PaymentStatusPage /></ProtectedRoute>} />

          {/* 404 */}
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>

      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover={false}
        pauseOnFocusLoss={false}
        draggable={false}
      />
    </>
  );
}

export default App;
